/** Social Security, RMD and FIRE maths. 2026 figures: SSA bend points $1,286 / $7,749, wage base $184,500. */
export const SS_BEND1 = 1286, SS_BEND2 = 7749, SS_WAGE_BASE_2026 = 184500;

/** Full retirement age in months, from the birth year (SSA table). */
export function fraMonths(birthYear: number): number {
  if (birthYear <= 1937) return 65 * 12;
  if (birthYear <= 1942) return 65 * 12 + 2 * (birthYear - 1937);
  if (birthYear <= 1954) return 66 * 12;
  if (birthYear <= 1959) return 66 * 12 + 2 * (birthYear - 1954);
  return 67 * 12;
}
export function fraLabel(birthYear: number): string {
  const m = fraMonths(birthYear), y = Math.floor(m / 12), r = m % 12;
  return r ? `${y} and ${r} months` : `${y}`;
}
/** Delayed-retirement credit per year past FRA, by birth year. */
export function delayedCreditPerYear(birthYear: number): number {
  if (birthYear >= 1943) return 8;
  if (birthYear >= 1941) return 7.5;
  if (birthYear >= 1939) return 7;
  if (birthYear >= 1937) return 6.5;
  if (birthYear >= 1935) return 6;
  return 5.5;
}

export function aimeFrom(annualEarnings: number, yearsWorked: number): number {
  const capped = Math.min(Math.max(0, annualEarnings), SS_WAGE_BASE_2026);
  return (capped / 12) * (Math.min(Math.max(0, yearsWorked), 35) / 35);
}
export function piaFrom(aime: number): number {
  const pia = aime <= SS_BEND1 ? aime * 0.9 : aime <= SS_BEND2 ? SS_BEND1 * 0.9 + (aime - SS_BEND1) * 0.32 : SS_BEND1 * 0.9 + (SS_BEND2 - SS_BEND1) * 0.32 + (aime - SS_BEND2) * 0.15;
  return Math.floor(pia * 10) / 10;
}
/** Monthly benefit when claiming at `claimMonths` (age in months). */
export function benefitAt(pia: number, birthYear: number, claimMonths: number): number {
  const fra = fraMonths(birthYear), diff = claimMonths - fra;
  if (diff < 0) { const e = -diff, f36 = Math.min(36, e), rest = Math.max(0, e - 36); return pia * (1 - (f36 * 5) / 9 / 100 - (rest * 5) / 12 / 100); }
  const credit = Math.min(diff, 70 * 12 - fra);
  return pia * (1 + (credit * delayedCreditPerYear(birthYear)) / 12 / 100);
}
export const hasCredits = (yearsWorked: number) => yearsWorked >= 10;

// IRS Uniform Lifetime Table (2022 onward)
export const UNIFORM_LIFETIME: Record<number, number> = {
  72: 27.4, 73: 26.5, 74: 25.5, 75: 24.6, 76: 23.7, 77: 22.9, 78: 22.0, 79: 21.1, 80: 20.2, 81: 19.4, 82: 18.5, 83: 17.7, 84: 16.8, 85: 16.0,
  86: 15.2, 87: 14.4, 88: 13.7, 89: 12.9, 90: 12.2, 91: 11.5, 92: 10.8, 93: 10.1, 94: 9.5, 95: 8.9, 96: 8.4, 97: 7.8, 98: 7.3, 99: 6.8, 100: 6.4,
  101: 6.0, 102: 5.6, 103: 5.2, 104: 4.9, 105: 4.6, 106: 4.3, 107: 4.1, 108: 3.9, 109: 3.7, 110: 3.5, 111: 3.4, 112: 3.3, 113: 3.1, 114: 3.0, 115: 2.9,
  116: 2.8, 117: 2.7, 118: 2.5, 119: 2.3, 120: 2.0,
};
/** Age at which RMDs begin (SECURE 2.0): 73 if born 1951-1959, 75 if born 1960 or later, 72 before. */
export function rmdStartAge(birthYear: number): number { return birthYear >= 1960 ? 75 : birthYear >= 1951 ? 73 : 72; }
export function rmdFor(balance: number, ageThisYear: number): { rmd: number; divisor: number } {
  const a = Math.min(120, Math.max(72, Math.round(ageThisYear)));
  const divisor = UNIFORM_LIFETIME[a];
  return { rmd: balance / divisor, divisor };
}

/**
 * Default inflation assumption (percent a year) shared by every retirement-planning tool: the 401(k) calculator, the FIRE
 * calculator and the retirement projection API. 2.5% sits between the Federal Reserve's 2% long-run target and the roughly
 * 3% US CPI average since the 1920s; people can always change it, and the result says which figure it used.
 */
export const DEFAULT_INFLATION_PCT = 2.5;

export interface FireInput { expenses: number; savings: number; annualSaving: number; returnPct: number; inflationPct: number; withdrawalPct: number }
export interface FireResult { target: number; years: number | null; realReturnPct: number }
/** Real return = (1+r)/(1+i)-1, so the target stays in today's dollars. `years` is fractional (null if more than 100). */
export function fire(i: FireInput): FireResult {
  const real = (1 + i.returnPct / 100) / (1 + i.inflationPct / 100) - 1;
  const target = i.expenses / (i.withdrawalPct / 100);
  let bal = i.savings;
  if (bal >= target) return { target, years: 0, realReturnPct: real * 100 };
  for (let y = 1; y <= 100; y++) {
    const next = bal * (1 + real) + i.annualSaving;
    if (next >= target) return { target, years: y - 1 + (target - bal) / (next - bal), realReturnPct: real * 100 };
    bal = next;
  }
  return { target, years: null, realReturnPct: real * 100 };
}
